import React from 'react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle, FaFileInvoiceDollar, FaChevronRight } from 'react-icons/fa';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Spinner from '../../common/Spinner';

const MAX_FILAS_DEUDA = 3;

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value || 0);

const formatFechaCorta = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '—';
  }
};

const DashboardDeudaEjecutivo = ({
  loading,
  error,
  resumen,
  clientes,
  diasCriterio,
  onReintentar
}) => {
  const cantidad = resumen?.cantidadClientes ?? 0;
  const totalAdeudado = resumen?.totalAdeudado ?? 0;
  const totalVencido = resumen?.totalVencido ?? 0;
  const todos = clientes || [];
  const listado = todos.slice(0, MAX_FILAS_DEUDA);
  const hayMas = todos.length > MAX_FILAS_DEUDA;

  return (
    <Card title="Clientes con deuda" icon={<FaFileInvoiceDollar />}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs sm:text-sm text-e0e3-muted leading-snug">
          Vencimiento: <strong className="text-e0e3-foreground">{diasCriterio} días</strong>
          {' · '}
          <Link to="/configuracion/empresa" className="text-e0e3-accent hover:underline">
            Configurar
          </Link>
        </p>
        {onReintentar && (
          <Button color="secondary" size="sm" onClick={onReintentar} disabled={loading}>
            Actualizar
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center py-5">
          <Spinner size="md" />
          <span className="ml-2 text-sm text-e0e3-muted">Cargando deudas…</span>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
            <div className="rounded-lg border border-e0e3-border/30 bg-e0e3-surface/30 px-2.5 py-2 sm:px-3 sm:py-2.5 min-w-0">
              <p className="text-[10px] sm:text-xs text-e0e3-muted truncate">Clientes</p>
              <p className="text-lg sm:text-xl font-bold text-e0e3-foreground leading-tight">{cantidad}</p>
            </div>
            <div className="rounded-lg border border-e0e3-border/30 bg-e0e3-surface/30 px-2.5 py-2 sm:px-3 sm:py-2.5 min-w-0">
              <p className="text-[10px] sm:text-xs text-e0e3-muted truncate">Adeudado</p>
              <p className="text-sm sm:text-lg font-bold text-e0e3-foreground leading-tight truncate" title={formatMoney(totalAdeudado)}>
                {formatMoney(totalAdeudado)}
              </p>
            </div>
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-2.5 py-2 sm:px-3 sm:py-2.5 min-w-0">
              <p className="text-[10px] sm:text-xs text-red-200/80 truncate flex items-center gap-0.5">
                <FaExclamationTriangle size={9} className="shrink-0" />
                Vencido
              </p>
              <p className="text-sm sm:text-lg font-bold text-red-200 leading-tight truncate" title={formatMoney(totalVencido)}>
                {formatMoney(totalVencido)}
              </p>
            </div>
          </div>

          {cantidad === 0 ? (
            <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-4 text-center text-sm text-emerald-200">
              Sin clientes con saldo pendiente en cuenta corriente.
            </div>
          ) : (
            <div className="space-y-2">
              {listado.map((cliente) => (
                <div
                  key={cliente.id}
                  className="rounded-lg border border-e0e3-border/25 bg-e0e3-surface/20 px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-e0e3-foreground truncate" title={cliente.nombre}>
                        {cliente.nombre}
                      </p>
                      <p className="mt-0.5 text-xs text-e0e3-muted">
                        Deuda: <span className="text-e0e3-foreground font-medium">{formatMoney(cliente.totalDeuda)}</span>
                        {Number(cliente.montoVencido) > 0 && (
                          <>
                            {' · '}
                            <span className="text-red-200">Vencido {formatMoney(cliente.montoVencido)}</span>
                          </>
                        )}
                      </p>
                      <p className="mt-0.5 text-[11px] text-e0e3-muted hidden sm:block">
                        Antigüedad: {formatFechaCorta(cliente.fechaDeudaMasAntigua)} · {cliente.diasAtraso} días
                      </p>
                    </div>
                    <Link
                      to={`/clientes/${cliente.id}/cuenta-corriente`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-xs font-medium text-e0e3-accent hover:bg-white/[0.08]"
                    >
                      Ver
                      <FaChevronRight size={9} />
                    </Link>
                  </div>
                </div>
              ))}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1 border-t border-e0e3-border/20">
                {hayMas ? (
                  <p className="text-xs text-e0e3-muted">
                    Mostrando {listado.length} de {todos.length} clientes con deuda.
                  </p>
                ) : (
                  <span className="text-xs text-e0e3-muted hidden sm:inline" />
                )}
                <Link
                  to="/clientes"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-e0e3-accent/30 bg-e0e3-accent/10 px-3 py-1.5 text-xs sm:text-sm font-semibold text-e0e3-accent hover:bg-e0e3-accent/20 transition-colors w-full sm:w-auto"
                >
                  Ver cuenta corriente
                  <FaChevronRight size={10} />
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default DashboardDeudaEjecutivo;
